/**
 * wayai sync-mcp --connection <name> [--hub <id|name>] — re-discover an MCP
 * connection's tool/resource catalog.
 *
 * `wayai push` only discovers MCP tools that are *new* to the hub catalog; it
 * never refreshes the cached input-schema of tools already catalogued. When an
 * external MCP server changes a tool's schema (e.g. adds typed params), the
 * agent keeps calling the stale schema until the catalog is re-synced. This is
 * the explicit, connection-level refresh for that case (gh #2548).
 *
 * Preview-only — production hubs are read-only (changes flow preview → sync).
 */

import { requireAuth } from '../lib/auth.js';
import { ApiClient } from '../lib/api-client.js';
import { requireRepoConfig } from '../lib/repo-config.js';
import { resolveActiveHubId } from '../lib/workspace.js';
import { printWarnings } from './push.js';

export function parseArgs(args: string[]): { connection: string | undefined } {
  let connection: string | undefined;
  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    if (arg === '--connection' && args[i + 1]) {
      connection = args[++i];
    }
  }
  return { connection };
}

export async function syncMcpCommand(args: string[]): Promise<void> {
  // resolveActiveHubId parses and removes `--hub <value>` from args.
  const hubId = resolveActiveHubId(args);
  const { connection } = parseArgs(args);

  if (!connection) {
    console.error('Usage: wayai sync-mcp --connection <name> [--hub <uuid|folder-name>]');
    console.error('');
    console.error('Re-discovers the MCP connection\'s tools/resources and refreshes their');
    console.error('cached input schemas. `--connection` accepts the connection display name');
    console.error('(as in connections[].name) or its UUID.');
    process.exit(1);
  }

  const { organization_id: organizationId } = requireRepoConfig();
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  console.log(`Syncing MCP tools for connection "${connection}"...`);
  const result = await client.syncMcp(hubId, connection, organizationId);

  console.log(
    result.message ??
      `Synced ${result.tools_count ?? 0} tool(s) and ${result.resources_count ?? 0} resource(s) from MCP server`,
  );

  if (result.warnings && result.warnings.length > 0) {
    printWarnings(result.warnings);
  }
}
